import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Card, Badge, Button, Modal, Field, Input, Select, Textarea, DateText, KeyValue } from '../components/ui';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FormError } from '../components/forms/common';
import { Can } from '../components/Can';
import { useCan } from '../hooks/usePermission';
import type { TaxRate, TaxDefaults, CreateTaxRateBody, UpdateTaxRateBody } from '../types/api';

/** UK only on screen for now; the AE rows stay in the table and keep working for AE companies. */
const REGION = 'UK' as const;
const today = () => new Date().toISOString().slice(0, 10);
const INVOICE_TYPE_LABEL: Record<string, string> = { RENT: 'Rent', SERVICE_CHARGE: 'Service charge', RECHARGE: 'Recharge', DEPOSIT: 'Deposit', SALE: 'Sale' };

function status(r: TaxRate): { value: string; tone: 'good' | 'neutral' | 'critical' } {
  if (!r.isActive) return { value: 'Inactive', tone: 'critical' };
  if (r.inForce) return { value: 'In force', tone: 'good' };
  return r.effectiveFrom > today() ? { value: 'Scheduled', tone: 'neutral' } : { value: 'Ended', tone: 'neutral' };
}

/**
 * VAT rates as data. A rate never changes once it exists: a new rate is a new row from the day it takes
 * effect, and every invoice and bill is taxed at the row in force on its own date.
 */
export default function TaxRatesPage() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TaxRate | null>(null);
  const rates = useQuery({
    queryKey: ['/tax-rates', REGION, showInactive],
    queryFn: async () => (await api.get<TaxRate[]>('/tax-rates', { region: REGION, includeInactive: showInactive ? 'true' : undefined })).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['/tax-rates'] });

  const columns: Column<TaxRate>[] = [
    { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-[12px] text-ink-900">{r.code}</span> },
    { key: 'label', label: 'Label' },
    { key: 'ratePercent', label: 'Rate %', align: 'right', render: (r) => <span>{r.ratePercent.toFixed(2).replace(/\.00$/, '')}%{!r.taxable && <span className="ml-1.5 text-[11px] text-ink-400">exempt</span>}</span> },
    { key: 'effectiveFrom', label: 'Effective from', render: (r) => <DateText value={r.effectiveFrom} /> },
    { key: 'effectiveTo', label: 'Effective to', render: (r) => r.effectiveTo ? <DateText value={r.effectiveTo} /> : <span className="text-ink-300">—</span> },
    { key: 'isDefault', label: 'Default?', sortValue: (r) => (r.isDefault ? 1 : 0), render: (r) => r.isDefault ? <Badge value="Default" tone="good" /> : <span className="text-ink-300">—</span> },
    { key: 'isActive', label: 'Active', sortValue: (r) => status(r).value, render: (r) => { const s = status(r); return <Badge value={s.value} tone={s.tone} />; } },
  ];

  return (
    <div className="space-y-2.5">
      <PageHeader title="Tax rates" menu={[{ label: 'New rate', icon: 'Plus', can: 'taxRates.create', onClick: () => setCreating(true) }]} />
      <DataTable columns={columns} rows={rates.data ?? []} page={1} onPage={() => undefined} isLoading={rates.isLoading} isFetching={rates.isFetching}
        rowKey={(r) => r.id} onRowClick={(r) => setEditing(r)} emptyTitle="No tax rates" emptyBody="Add a rate with New rate."
        filters={<label className="flex items-center gap-1.5 px-1 text-[12.5px] text-ink-600"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>} />
      {rates.error && <FormError error={rates.error} />}

      <DefaultsCard rates={rates.data ?? []} />

      {creating && <NewRateModal codes={[...new Set((rates.data ?? []).map((r) => r.code))]} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); void refresh(); }} />}
      {editing && <EditRateModal rate={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); void qc.invalidateQueries({ queryKey: ['/tax-rates/defaults'] }); }} />}
    </div>
  );
}

function NewRateModal({ codes, onClose, onSaved }: { codes: string[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ code: '', label: '', rate: '', effectiveFrom: today(), notes: '' });
  const save = useMutation({
    mutationFn: async () => {
      const body: CreateTaxRateBody = { region: REGION, code: form.code.trim().toUpperCase(), label: form.label.trim(), ratePercent: Number(form.rate), effectiveFrom: form.effectiveFrom, notes: form.notes.trim() || null };
      return api.post<TaxRate>('/tax-rates', body);
    },
    onSuccess: onSaved,
  });
  const valid = form.code.trim() && form.label.trim() && form.rate !== '' && Number.isFinite(Number(form.rate)) && form.effectiveFrom;
  return (
    <Modal open onClose={onClose} title="New tax rate"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>Add rate</Button></>}>
      <div className="space-y-3">
        <p className="text-[12.5px] text-ink-500">To change a rate, first give the current one an effective-to date, then add the new rate from the next day. Invoices and bills are taxed at the rate in force on their own date.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" hint="An existing code, or a new one (upper case)">
            <Input list="tax-rate-codes" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="STANDARD" className="w-full font-mono" />
            <datalist id="tax-rate-codes">{codes.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Rate %"><Input type="number" min={0} max={100} step={0.01} value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="20" className="w-full" /></Field>
        </div>
        <Field label="Label"><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Standard rate (20%)" className="w-full" /></Field>
        <Field label="Effective from"><Input type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} className="w-full" /></Field>
        <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

function EditRateModal({ rate, onClose, onSaved }: { rate: TaxRate; onClose: () => void; onSaved: () => void }) {
  const canEdit = useCan('taxRates.update');
  const [form, setForm] = useState({ label: rate.label, effectiveTo: rate.effectiveTo ?? '', notes: rate.notes ?? '', isActive: rate.isActive, isDefault: rate.isDefault });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const body: UpdateTaxRateBody = {};
      if (form.label.trim() !== rate.label) body.label = form.label.trim();
      if ((form.effectiveTo || null) !== rate.effectiveTo) body.effectiveTo = form.effectiveTo || null;
      if ((form.notes.trim() || null) !== rate.notes) body.notes = form.notes.trim() || null;
      if (form.isActive !== rate.isActive) body.isActive = form.isActive;
      if (form.isDefault !== rate.isDefault) body.isDefault = form.isDefault;
      return api.patch<TaxRate>(`/tax-rates/${rate.id}`, body);
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: async () => api.delete(`/tax-rates/${rate.id}`), onSuccess: onSaved });
  const dirty = form.label.trim() !== rate.label || (form.effectiveTo || null) !== rate.effectiveTo || (form.notes.trim() || null) !== rate.notes || form.isActive !== rate.isActive || form.isDefault !== rate.isDefault;

  return (
    <Modal open onClose={onClose} title={<span><span className="font-mono">{rate.code}</span> · {rate.ratePercent}%</span>}
      footer={<>
        <Can do="taxRates.delete">{confirmDelete
          ? <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending} className="mr-auto">Confirm delete</Button>
          : <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="mr-auto">Delete</Button>}</Can>
        <Button variant="secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
        {canEdit && <Button onClick={() => save.mutate()} disabled={!dirty || !form.label.trim() || save.isPending}>Save</Button>}
      </>}>
      <div className="space-y-3">
        <KeyValue items={[
          { label: 'Rate', value: `${rate.ratePercent}%${rate.taxable ? '' : ' (exempt)'}` },
          { label: 'Effective from', value: <DateText value={rate.effectiveFrom} /> },
          { label: 'Input tax', value: rate.restrictsInputRecovery ? 'Restricts recovery' : 'Recoverable' },
        ]} />
        <p className="text-[12px] text-ink-500">The rate and its start date are fixed. For a different rate, end this one with an effective-to date and add a new rate.</p>
        <Field label="Label"><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} disabled={!canEdit} className="w-full" /></Field>
        <Field label="Effective to" hint="The last day this rate applies. Leave empty while it is current.">
          <Input type="date" min={rate.effectiveFrom} value={form.effectiveTo} onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })} disabled={!canEdit} className="w-full" />
        </Field>
        <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!canEdit} className="w-full" /></Field>
        <div className="flex flex-wrap gap-4 text-[13px] text-ink-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} disabled={!canEdit} /> Active</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} disabled={!canEdit} /> Default code (used for bills and any invoice type without its own default)</label>
        </div>
        <FormError error={save.error ?? remove.error} />
      </div>
    </Modal>
  );
}

function DefaultsCard({ rates }: { rates: TaxRate[] }) {
  const qc = useQueryClient();
  const canEdit = useCan('taxRates.update');
  const defaults = useQuery({ queryKey: ['/tax-rates/defaults', REGION], queryFn: async () => (await api.get<TaxDefaults>('/tax-rates/defaults', { region: REGION })).data });
  /** Edits not yet saved, by invoice type; the saved mapping shows through everywhere else. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const valueOf = (m: TaxDefaults['mappings'][number]) => edits[m.invoiceType] ?? m.code ?? '';
  const codes = useMemo(() => [...new Set(rates.filter((r) => r.isActive).map((r) => r.code))].sort(), [rates]);
  const dirty = !!defaults.data?.mappings.some((m) => valueOf(m) !== (m.code ?? ''));
  const save = useMutation({
    mutationFn: async () => api.put<TaxDefaults>('/tax-rates/defaults', {
      region: REGION, mappings: (defaults.data?.mappings ?? []).filter((m) => valueOf(m)).map((m) => ({ invoiceType: m.invoiceType, code: valueOf(m) })),
    }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['/tax-rates/defaults'] }); setEdits({}); },
  });

  return (
    <Card title="Defaults by invoice type" subtitle="The code a new invoice line takes when none is chosen. Credit notes always copy the code of the invoice they credit."
      actions={canEdit ? <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>Save</Button> : undefined}>
      {defaults.isLoading ? <div className="h-32 animate-pulse rounded-lg bg-ink-50" /> : (
        <div className="space-y-3">
          <table className="w-full text-[12.5px]">
            <thead><tr className="bg-ink-50/60 text-left text-[11.5px] uppercase tracking-wide text-ink-500"><th className="px-3 py-1.5 font-medium">Invoice type</th><th className="px-3 py-1.5 font-medium">Tax code</th></tr></thead>
            <tbody>{defaults.data?.mappings.map((m) => (
              <tr key={m.invoiceType} className="border-b border-ink-100 last:border-0">
                <td className="border-t border-ink-100 px-3 py-1.5 text-ink-800">{INVOICE_TYPE_LABEL[m.invoiceType] ?? m.invoiceType}</td>
                <td className="border-t border-ink-100 px-3 py-1.5">
                  <Select value={valueOf(m)} onChange={(e) => setEdits({ ...edits, [m.invoiceType]: e.target.value })} disabled={!canEdit} className="w-56" aria-label={`Tax code for ${m.invoiceType}`}>
                    {!valueOf(m) && <option value="">Default ({defaults.data?.fallbackCode ?? 'none'})</option>}
                    {codes.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </td>
              </tr>
            ))}</tbody>
          </table>
          <p className="text-[12px] text-ink-500">Bills, and any invoice type not listed, use the default code: <span className="font-mono text-ink-800">{defaults.data?.fallbackCode ?? 'none set'}</span>.</p>
          <FormError error={save.error ?? defaults.error} />
        </div>
      )}
    </Card>
  );
}
